import React, { useState } from "react";
import { IconButton, Tooltip, Menu, MenuItem, Box } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";
import { AUTH_TOGGLE_ICON_PX, authToggleButtonShellSx } from "./authToggleConstants";
import { getMm } from "../theme/modernMaterialEnterprise";

const LanguageToggleButton = ({
  size = "medium",
  position = "fixed",
  embedded = false,
  sx = {},
  showOnlyOnLogin = true,
}) => {
  const { i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const currentLanguage = i18n.language;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const isLoggedIn = localStorage.getItem("realmJwt") || localStorage.getItem("supabaseToken");

  if (showOnlyOnLogin && isLoggedIn) {
    return null;
  }

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧", native: "EN" },
    { code: "sv", name: "Svenska", flag: "🇸🇪", native: "SV" },
    { code: "pt", name: "Português", flag: "🇧🇷", native: "PT" },
    { code: "es", name: "Español", flag: "🇪🇸", native: "ES" },
  ];

  const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    try {
      localStorage.setItem("language", languageCode);
    } catch (error) {
      console.warn("Failed to save language preference to localStorage:", error);
    }
    handleClose();
  };

  const defaultSx = {
    ...(embedded || position === "static"
      ? {}
      : {
          position: "fixed",
          top: 16,
          right: 68,
          zIndex: 1000,
        }),
    ...authToggleButtonShellSx(isDarkMode),
    ...sx,
  };

  return (
    <>
      <Tooltip title={`Change language (${currentLang.name})`}>
        <IconButton onClick={handleClick} size={size} sx={defaultSx} aria-label="change language">
          <Box
            sx={{
              width: AUTH_TOGGLE_ICON_PX,
              height: AUTH_TOGGLE_ICON_PX,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: AUTH_TOGGLE_ICON_PX,
              lineHeight: 1,
            }}
          >
            {currentLang.flag}
          </Box>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "rgba(0,0,0,0.95)" : "rgba(255,255,255,0.95)",
            backdropFilter: "blur(20px)",
            border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
            borderRadius: "12px",
            boxShadow: isDarkMode ? "0 8px 32px rgba(0,0,0,0.3)" : "0 8px 32px rgba(0,0,0,0.1)",
            mt: 1,
            minWidth: 180,
            overflow: "hidden",
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              gap: 1.5,
              backgroundColor:
                language.code === currentLanguage
                  ? getMm(isDarkMode).hoverWash
                  : "transparent",
              "&:hover": {
                backgroundColor: getMm(isDarkMode).hoverSubtle,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
              <Box sx={{ fontSize: "1.5rem" }}>{language.flag}</Box>
              <Box
                sx={{
                  color: getMm(isDarkMode).primary,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                {language.native} - {language.name}
              </Box>
            </Box>
            {language.code === currentLanguage && (
              <CheckIcon sx={{ color: "#4caf50", fontSize: "1.2rem" }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageToggleButton;
